import React, {useEffect, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import Input from '../../components/Input';
import Select from 'react-select';
import ManagementRow from '../../components/ManagementRow';
import {
  useCreateUserMutation,
  useGetAgencyQuery,
  useGetUsersRolesQuery,
  useInviteFboMutation,
  useLazyGetAgencyQuery,
  useLazyGetUsersIndexQuery,
  useLazyGetUsersRolesQuery,
  useLazyRegionsListQuery,
  useRegionsListQuery,
} from '../../store/builder/inspections.api';
import {useDispatch, useSelector} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import {toast} from 'react-toastify';
import {validationEmail} from '../../global/validate';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {Helmet} from 'react-helmet';
import CustomPaginate from '../../components/PaginateComponent';
import {useDebounce} from '../LogSheets/hooks';
import useLoader from '../../global/UseLoadingHandler';
import {useTranslation} from '../../hooks/useTranslation';
import {useLocales} from '../../hooks/useLanguages';

export default function UserManagement() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [createModal, setCreateModal] = useState(false);
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const {data: localesData} = useLocales({
    enabled: userInfo?.is_global,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentUser, setCurrentUser] = useState(null);
  const [rolesOptions, setRolesOptions] = useState([]);
  const debouncedSearchValue = useDebounce(search, 500);
  const [userCreateData, setUserCreateData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    role_ids: [],
    agency_ids: [],
  });
  const [userInviteData, setUserInviteData] = useState({
    email: '',
    role_id: null,
    agency_id: null,
    region_id: null,
    api_access_only: false,
    locale_ids: [],
  });

  const [dataErrors, setDataErrors] = useState({
    firstNameError: false,
    lastNameError: false,
    emailError: false,
    passwordError: false,
    rolesError: false,
    agencyError: false,
  });
  const [chosenRoles, setChosenRoles] = useState([]);
  const [chosenAgency, setChosenAgency] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [userList, setUserList] = useState([]);
  const [trigerGetUsers, setTrigerGetUsers] = useState(false);

  const [
    getUsersData,
    {
      data: userData,
      isSuccess: userIsSuccess,
      isFetching: userIsLoading,
      isError: userIsError,
      error: userError,
    },
  ] = useLazyGetUsersIndexQuery();

  const [
    getAgencies,
    {
      data: agencyData,
      error: agencyError,
      isLoading: agencyIsLoading,
      isSuccess: agencyIsSuccess,
    },
  ] = useLazyGetAgencyQuery();

  const [
    getRegions,
    {
      data: regionsData,
      error: regionsError,
      isLoading: regionsIsLoading,
      isSuccess: regionsIsSuccess,
    },
  ] = useLazyRegionsListQuery();

  const [
    getUserRoles,
    {
      data: userRolesData,
      isSuccess: userRolesIsSuccess,
      isLoading: userRolesIsLoading,
      isError: userRolesIsError,
      error: userRolesError,
    },
  ] = useLazyGetUsersRolesQuery();

  const [
    createUser,
    {
      data: createUserData,
      isSuccess: createUserIsSuccess,
      isLoading: createUserIsLoading,
      isError: createUserIsError,
      error: createUserError,
    },
  ] = useCreateUserMutation();

  const [
    invite,
    {
      data: inviteData,
      isLoading: inviteIsLoading,
      isError: inviteIsError,
      error: inviteError,
      isSuccess: inviteIsSuccess,
    },
  ] = useInviteFboMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    createUserError,
    userRolesError,
    userError,
    agencyError,
    inviteError,
  ]);

  useLoader([
    userIsLoading,
    userRolesIsLoading,
    createUserIsLoading,
    agencyIsLoading,
    inviteIsLoading,
  ]);

  useEffect(() => {
    getAgencies();
    getUserRoles();
    if (userInfo?.is_corporate) {
      getRegions();
    }
  }, []);

  const getUsers = () => {
    getUsersData({
      agency_id: chosenAgency?.value,
      page: currentPage,
      search: debouncedSearchValue,
      filter: statusFilter,
    });
  };

  useEffect(() => {
    getUsers();
  }, [chosenAgency, currentPage, debouncedSearchValue, trigerGetUsers, statusFilter]);

  useEffect(() => {
    if (userInfo?.is_corporate) {
      setRolesOptions(
        userRolesData?.map(role => ({
          value: role.id,
          label: role.name,
        })),
      );
    } else {
      setRolesOptions(
        userRolesData
          ?.filter(
            el => el?.key !== 'corporate' && el?.key !== 'regional_corporate',
          )
          ?.map(role => ({
            value: role.id,
            label: role.name,
          })),
      );
    }
  }, [userRolesData]);

  useEffect(() => {
    if (createModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [createModal]);

  useEffect(() => {
    if (dataErrors.nameError) {
      toast.error(t('userManagement.messages.enterCorrectName'));
    }
    if (dataErrors.emailError) {
      toast.error(t('userManagement.messages.enterCorrectEmail'));
    }
    if (dataErrors.passwordError) {
      toast.error(t('userManagement.messages.enterCorrectPassword'));
    }
    if (dataErrors.rolesError) {
      toast.error(t('userManagement.messages.chooseAnyRole'));
    }
    if (dataErrors.agencyError) {
      toast.error(t('userManagement.messages.chooseAnyFbo'));
    }
  }, [dataErrors, t]);

  useEffect(() => {
    if (userData?.data?.length > 0) {
      setUserList(userData?.data?.map(el => ({...el, action_status: false})));
    } else {
      setUserList([]);
    }
  }, [userData]);

  useEffect(() => {
    if (inviteIsSuccess) {
      toast.success(t('userManagement.messages.inviteSuccess'));
      setCreateModal(false);
      setUserInviteData({
        email: '',
        role_id: null,
        agency_id: null,
        region_id: null,
        locale_ids: [],
      });
    }
    if (inviteIsError) {
      dispatch(setLoader(false));
      toast.error(inviteError?.data?.email?.[0]);
    }
  }, [inviteIsSuccess, inviteError]);

  useEffect(() => {
    return window.addEventListener('click', e => {
      if (
        e.target.closest('.userSettings') === null &&
        e.target.closest('.management__table-settings') === null
      ) {
        setUserList(old => {
          let newList = [...old];
          return newList.map(el => ({...el, action_status: false}));
        });
        return;
      }
    });
  }, []);

  const validateCreate = () => {
    if (!userCreateData?.first_name && userCreateData?.first_name?.length < 3) {
      setDataErrors(old => ({...old, firstNameError: true}));
    } else {
      setDataErrors(old => ({...old, firstNameError: false}));
    }
    if (!userCreateData?.last_name && userCreateData?.last_name?.length < 3) {
      setDataErrors(old => ({...old, lastNameError: true}));
    } else {
      setDataErrors(old => ({...old, lastNameError: false}));
    }
    if (!userCreateData?.email && !validationEmail(userCreateData?.email)) {
      setDataErrors(old => ({...old, emailError: true}));
    } else {
      setDataErrors(old => ({...old, emailError: false}));
    }
    if (userCreateData?.password !== userCreateData?.confirm_password) {
      setDataErrors(old => ({...old, passwordError: true}));
    } else {
      setDataErrors(old => ({...old, passwordError: false}));
    }
    if (chosenRoles?.length === 0) {
      setDataErrors(old => ({...old, rolesError: true}));
    } else {
      setDataErrors(old => ({...old, rolesError: false}));
    }
    if (chosenAgency?.length === 0) {
      setDataErrors(old => ({...old, agencyError: true}));
    } else {
      setDataErrors(old => ({...old, agencyError: false}));
    }
  };

  const localesOptions = localesData?.map(locale => ({
    value: locale.id,
    label: (
      <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
        <img width={20} height={20} src={locale.url} alt={locale.name} />
        {locale.name}
      </div>
    ),
  }));

  const handleNewCreateEmail = e => {
    setUserInviteData(old => ({...old, email: e.target.value}));
  };
  const handleNewCreateRole = e => {
    setUserInviteData(old => ({...old, role_id: e.value}));
  };
  const handleNewCreateAgency = e => {
    setUserInviteData(old => ({...old, agency_id: e.value}));
  };
  const handleNewCreateRegion = e => {
    setUserInviteData(old => ({...old, region_id: e.value}));
  };
  const handleNewCreateAccess = e => {
    setUserInviteData(old => ({...old, api_access_only: e.target.checked}));
  };
  const handleNewCreateLocale = e => {
    setUserInviteData(old => ({
      ...old,
      locale_ids: e.map(option => option.value),
    }));
  };
  const submitNewCreate = e => {
    e.preventDefault();
    if (!validationEmail(userInviteData?.email)) {
      toast.error(t('userManagement.messages.enterCorrectEmail'));
      return;
    }
    if (!userInviteData?.role_id) {
      toast.error(t('userManagement.messages.chooseRole'));
      return;
    }
    if (
      userInfo?.is_global &&
      userInviteData?.role_id !== 10 &&
      !userInviteData?.agency_id
    ) {
      toast.error(t('userManagement.messages.chooseFbo'));
      return;
    }
    if (userInfo?.is_global && userInviteData?.role_id === 10) {
      if (
        !userInviteData?.locale_ids ||
        userInviteData?.locale_ids?.length === 0
      ) {
        toast.error(t('userManagement.messages.chooseLocale'));
        return;
      }
    }
    if (userInfo?.is_corporate) {
      if (
        userInviteData?.role_id !== 8 &&
        userInviteData?.role_id !== 9 &&
        userInviteData?.role_id !== 10
      ) {
        if (!userInviteData?.agency_id) {
          toast.error(t('userManagement.messages.chooseFbo'));
          return;
        }
      } else if (userInviteData?.role_id === 9) {
        if (!userInviteData?.region_id) {
          toast.error(t('userManagement.messages.chooseRegion'));
          return;
        }
      } else if (userInviteData?.role_id === 10) {
        if (
          !userInviteData?.locale_ids ||
          userInviteData?.locale_ids?.length === 0
        ) {
          toast.error(t('userManagement.messages.chooseLocale'));
          return;
        }
      }
    }

    if (userInfo?.is_global) {
      invite({
        email: userInviteData?.email,
        role_id: userInviteData?.role_id,
        agency_id: userInviteData?.agency_id,
        locale_ids: userInviteData?.locale_ids,
      });
    } else if (userInfo?.is_corporate) {
      if (userInviteData?.role_id === 8) {
        invite({
          email: userInviteData?.email,
          role_id: userInviteData?.role_id,
          api_access_only: userInviteData?.api_access_only,
          locale_ids: userInviteData?.locale_ids,
        });
      } else if (userInviteData?.role_id === 9) {
        invite({
          email: userInviteData?.email,
          role_id: userInviteData?.role_id,
          region_id: userInviteData?.region_id,
          locale_ids: userInviteData?.locale_ids,
        });
      } else {
        invite({
          email: userInviteData?.email,
          role_id: userInviteData?.role_id,
          agency_id: userInviteData?.agency_id,
          locale_ids: userInviteData?.locale_ids,
        });
      }
    } else {
      if (userInviteData?.role_id === 8) {
        invite({
          email: userInviteData?.email,
          role_id: userInviteData?.role_id,
          api_access_only: userInviteData?.api_access_only,
          locale_ids: userInviteData?.locale_ids,
        });
      } else if (userInviteData?.role_id === 10) {
        if (
          !userInviteData?.locale_ids ||
          userInviteData?.locale_ids?.length === 0
        ) {
          toast.error(t('userManagement.messages.chooseLocale'));
          return;
        }
        invite({
          email: userInviteData?.email,
          role_id: userInviteData?.role_id,
          agency_id: userInfo?.agency?.id,
          locale_ids: userInviteData?.locale_ids,
        })
          .unwrap()
          .then()
          .catch(err => {
            toast.error(err?.data?.message);
          });
      } else {
        invite({
          email: userInviteData?.email,
          role_id: userInviteData?.role_id,
          agency_id: userInfo?.agency?.id,
          locale_ids: userInviteData?.locale_ids,
        })
          .unwrap()
          .then()
          .catch(err => {
            toast.error(err?.data?.message);
          });
      }
    }
  };

  const handleCreateChange = e => {
    setUserCreateData(old => ({...old, [e.target.name]: e.target.value}));
  };
  const handleRoles = e => {
    setChosenRoles(e);
  };

  const handleAgency = e => {
    setCurrentPage(1);
    setChosenAgency(e);
  };

  const handleCreateUser = e => {
    e.preventDefault();
    validateCreate();
    if (
      !dataErrors?.firstNameError &&
      !dataErrors?.lastNameError &&
      !dataErrors?.emailError &&
      !dataErrors?.rolesError &&
      !dataErrors?.passwordError &&
      !dataErrors?.agencyError &&
      userCreateData?.password === userCreateData?.confirm_password
    ) {
      createUser({
        first_name: userCreateData.first_name,
        last_name: userCreateData.last_name,
        email: userCreateData.email,
        password: userCreateData.password,
        role_id: chosenRoles?.value,
        agency_id: chosenAgency?.value,
      });
    }
  };

  const handleSearch = e => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = status => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const renderNewCreateModal = () => {
    return (
      <form className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('userManagement.inviteModal.title')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setCreateModal(false)}
            />
          </div>
          <div className='modal__content-body'>
            <Input
              containerWidth={'100%'}
              id='email'
              name='email'
              placeholder={t('userManagement.inviteModal.email')}
              label={t('userManagement.inviteModal.email')}
              type={'email'}
              onChange={handleNewCreateEmail}
              required={true}
            />
            <div className='customSelect'>
              <span className='customSelect__label'>
                {t('userManagement.inviteModal.userRole')}
              </span>
              <Select
                options={rolesOptions}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                    borderRadius: 8,
                    height: 42,
                    backgroundColor: '#F4F5F6',
                    marginTop: 8,
                  }),
                }}
                name={'role_id'}
                onChange={handleNewCreateRole}
                menuPosition='fixed'
                placeholder={t('userManagement.inviteModal.userRole')}
              />
            </div>
            {userInfo?.is_global && userInviteData?.role_id !== 10 && (
              <div className='customSelect'>
                <span className='customSelect__label'>
                  {t('userManagement.inviteModal.fbo')}
                </span>
                <Select
                  options={agencyData?.map(role => ({
                    value: role.id,
                    label: role.title,
                  }))}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                      borderRadius: 8,
                      height: 42,
                      backgroundColor: '#F4F5F6',
                      marginTop: 8,
                    }),
                  }}
                  name={'agency_ids'}
                  onChange={handleNewCreateAgency}
                  menuPosition='fixed'
                  placeholder={t('userManagement.inviteModal.fbo')}
                />
              </div>
            )}
            {userInfo?.is_corporate &&
              userInviteData?.role_id !== 8 &&
              userInviteData?.role_id !== 9 && (
                <div className='customSelect'>
                  <span className='customSelect__label'>
                    {t('userManagement.inviteModal.fbo')}
                  </span>
                  <Select
                    options={agencyData?.map(role => ({
                      value: role.id,
                      label: role.title,
                    }))}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                        borderRadius: 8,
                        height: 42,
                        backgroundColor: '#F4F5F6',
                        marginTop: 8,
                      }),
                    }}
                    name={'agency_ids'}
                    onChange={handleNewCreateAgency}
                    menuPosition='fixed'
                  />
                </div>
              )}
            {userInfo?.is_corporate && userInviteData?.role_id === 9 && (
              <div className='customSelect'>
                <span className='customSelect__label'>
                  {t('userManagement.inviteModal.regions')}
                </span>
                <Select
                  options={regionsData?.map(role => ({
                    value: role.id,
                    label: role.name,
                  }))}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                      borderRadius: 8,
                      height: 42,
                      backgroundColor: '#F4F5F6',
                      marginTop: 8,
                    }),
                  }}
                  name={'regions_ids'}
                  onChange={handleNewCreateRegion}
                  menuPosition={'absolute'}
                />
              </div>
            )}
            {userInviteData?.role_id === 8 && (
              <div className='customSelect'>
                <label className='checkbox-container'>
                  {t('userManagement.inviteModal.apiAccessOnly')}
                  <input
                    type={'checkbox'}
                    id={'api_access_only'}
                    name={'api_access_only'}
                    onChange={handleNewCreateAccess}
                  />
                  <span className='checkmark'></span>
                </label>
              </div>
            )}
            {userInviteData?.role_id === 10 && (
              <div className='customSelect'>
                <span className='customSelect__label'>
                  {t('userManagement.inviteModal.locale')}
                </span>
                <Select
                  options={localesOptions}
                  isMulti
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                      borderRadius: 8,
                      minHeight: 42,
                      backgroundColor: '#F4F5F6',
                      marginTop: 8,
                    }),
                  }}
                  name={'locale'}
                  onChange={handleNewCreateLocale}
                  menuPosition='fixed'
                  placeholder={t(
                    'userManagement.inviteModal.localePlaceholder',
                  )}
                />
              </div>
            )}
          </div>
          <div className='modal__content-bottom'>
            <button className='close' onClick={() => setCreateModal(false)}>
              {t('userManagement.inviteModal.close')}
            </button>
            <button className='save' onClick={submitNewCreate}>
              {t('userManagement.inviteModal.invite')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </form>
    );
  };

  const handlePageClick = page => {
    setCurrentPage(page.selected + 1);
    window.scrollTo(0, 0);
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('userManagement.pageTitle')}</title>
      </Helmet>
      {createModal && renderNewCreateModal()}
      <DashboardHeader
        title={t('userManagement.title')}
        showBtn={!userInfo?.corporate?.is_sso_active}
        type='management'
        action={() => setCreateModal(true)}
      />
      <div className='management management--userActivityColumns'>
          <div className='management__statusFilters'>
            <button
              type='button'
              className={`management__statusFilterBtn ${statusFilter === 'active' ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange('active')}
            >
              {t('dropdown.active')}
            </button>
            <button
              type='button'
              className={`management__statusFilterBtn ${statusFilter === 'inactive' ? 'active' : ''}`}
              onClick={() => handleStatusFilterChange('inactive')}
            >
              {t('dropdown.inactive')}
            </button>
          </div>
        <div className='management__header'>
          <Input
            type='text'
            placeholder={t('userManagement.searchPlaceholder')}
            image='/assets/icons/search.svg'
            maxwidth={264}
            onChange={handleSearch}
          />
          {(userInfo?.is_global ||
            userInfo?.is_corporate ||
            userInfo?.is_regional_corporate) && (
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable={true}
              options={agencyData?.map(role => ({
                value: role.id,
                label: role.title,
              }))}
              onChange={handleAgency}
              placeholder={t('userManagement.filters.byFbo')}
            />
          )}
        </div>
        <div className='management__box'>
          <div className='management__table'>
            <div className={`management__table-head${userInfo?.is_global && statusFilter === 'active' ? ' global' : ''}`}>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.profilePicture')}
              </div>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.name')}
              </div>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.email')}
              </div>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.fbo')}
              </div>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.role')}
              </div>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.ein')}
              </div>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.initials')}
              </div>
              <div className='management__table-th'>
                {t('userManagement.tableHeaders.signature')}
              </div>
              <div className='management__table-th management__userActivityCell'>
                {t('userManagement.tableHeaders.lastWebLogin')}
              </div>
              <div className='management__table-th management__userActivityCell'>
                {t('userManagement.tableHeaders.lastMobileLogin')}
              </div>
              <div className='management__table-th management__userActivityCell'>
                {statusFilter === 'active'
                  ? t('userManagement.tableHeaders.firstActiveAt')
                  : t('userManagement.tableHeaders.deactivatedAt')}
              </div>
              {userInfo?.is_global && statusFilter === 'active' && (
                <div className='management__table-th'></div>
              )}
              <div className='management__table-th'></div>
            </div>
            <div className='management__table-rows'>
              {
                userList?.length ? (
                  userList?.map((user, index) => (
                    <ManagementRow
                      key={user.id}
                      user={user}
                      userRolesData={userRolesData}
                      userAgencyData={agencyData}
                      lengthArray={filteredData?.length}
                      filteredData={filteredData}
                      setFilteredData={setFilteredData}
                      indexElem={index}
                      setCurrentUser={setCurrentUser}
                      currentUser={currentUser}
                      setUserList={setUserList}
                      trigerGetUsers={trigerGetUsers}
                      setTrigerGetUsers={setTrigerGetUsers}
                      getUsers={getUsers}
                      includeUserActivityColumns
                      statusFilter={statusFilter}
                    />
                  ))
                ) : (
                  <div className='emptyList'>
                    <img src='/assets/empty-list.png' alt='empty' />
                    <p>{t('userManagement.messages.noData')}</p>
                  </div>
                )
              }
            </div>
          </div>
          <CustomPaginate
            pageCount={userData?.pages}
            handlePageClick={handlePageClick}
            currentPage={currentPage}
          />
        </div>
      </div>
    </Wrapper>
  );
}
